import { useNavigate } from '@tanstack/react-location';
import clsx from 'clsx';
import mailcheck from 'mailcheck';
import React, { useCallback, useEffect, useState } from 'react';
import useAssessmentApi from '../../../api/use-assessment-api';
import { useFlexAssessmentContext } from '../../../context/context';
import buttons from '../../../css/buttons.module.scss';
import styles from '../../../css/modal.module.scss';
import { FlexAssessmentContextAction } from '../../../types/flex-assessment-context-action';
import { NotificationType } from '../../../types/notification-type';
import { RegistrationFieldName } from '../../../types/registration-field-name';
import { checkUserStatus } from '../../../utils/check-user-status';
import { PAGE_URLS, USER_EMAIL_STATUS_RES } from '../../../utils/constants';
import { logger } from '../../../utils/logger';
import { MIXPANEL_EVENTS } from '../../../utils/mixpanel-consts';
import { track } from '../../../utils/mixpanel-helper';
import { STRINGS } from '../../../utils/strings';
import { validateEmail } from '../../../utils/validation';
import EmailPrivacyNotification from '../../email-privacy-notification';
import LabelAndError from '../../form-fields/label-and-error';
import { useFieldStates } from '../../form-fields/use-field-states';
import Spinner, { SPINNER_COLORS } from '../../spinner';

const EMAIL_FIELD_NAME = 'email';

export const CheckEmailForm = React.memo((): JSX.Element => {
  const navigate = useNavigate();
  const { associateEmail } = useAssessmentApi();
  const { state, dispatch } = useFlexAssessmentContext();
  const [email, setEmail] = useState((state.email.value as string) || '');
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isEmailVerificationInProgress, setEmailVerificationInProgress] =
    useState<boolean>();
  const [message, setMessage] = useState<string>('');
  const [emailSuggestion, setEmailSuggestion] = useState<null | Record<
    string,
    any
  >>();
  const [isValid, setIsValid] = useState<undefined | boolean>();
  const validator = useCallback((): undefined | boolean => {
    return isValid;
  }, [isValid]);
  const { isPristine, onBlurHandler } = useFieldStates({
    validator,
    value: '',
  });
  const [emailCheckRes, setEmailCheckRes] = useState<string>('');

  const updateStore = useCallback(
    (isValid: boolean): void => {
      dispatch({
        type: FlexAssessmentContextAction.setFieldValue,
        field: RegistrationFieldName.email,
        value: email,
        isValid: isValid,
      });
    },
    [dispatch, email]
  );

  const validateEmailFields = useCallback(() => {
    if (!email || email.length === 0) {
      setMessage('');
      setIsValid(undefined);
      return;
    }

    if (!validateEmail(email)) {
      setMessage(STRINGS.ERRORS.EMAIL_NOT_VALID);
      setIsValid(false);
      return;
    }

    setEmailVerificationInProgress(true);

    checkUserStatus(email).then((res: string): void => {
      setEmailVerificationInProgress(false);
      setEmailCheckRes(res);

      if (res === USER_EMAIL_STATUS_RES.NOT_FOUND) {
        setMessage('');
        setIsValid(true);
        updateStore(true);
      } else if (res === USER_EMAIL_STATUS_RES.EXISTS) {
        setMessage('');
        setIsValid(true);
        updateStore(true);
      } else if (res === USER_EMAIL_STATUS_RES.NOT_VERIFIED) {
        setMessage(STRINGS.ERRORS.USER_NOT_CONFIRMED);
        setIsValid(false);
        updateStore(false);
      } else if (res === USER_EMAIL_STATUS_RES.NOT_INITIALIZED) {
        setMessage(STRINGS.ERRORS.USER_NOT_INITIALIZED);
        setIsValid(false);
        updateStore(false);
      }
    });
  }, [email, updateStore]);

  useEffect((): void => {
    validateEmailFields();
  }, [email, validateEmailFields]);

  const onUpdateHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const eventValue = e.currentTarget.value.trim();
    setEmail(eventValue);
  };

  const checkForTypos = (): void => {
    onBlurHandler();
    mailcheck.run({
      email,
      suggested: (suggestion: Record<string, any>) => {
        setEmailSuggestion(suggestion);
      },
      empty: () => {
        setEmailSuggestion(null);
      },
    });
  };

  const replaceEmailWithSuggestion = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
    if (emailSuggestion && emailSuggestion.full) {
      setEmail(emailSuggestion.full);
      setIsValid(isValid);
      setEmailSuggestion(null);
    }
  };

  const submit = async (
    event: React.FormEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    switch (emailCheckRes) {
      case USER_EMAIL_STATUS_RES.NOT_FOUND:
        try {
          // User answered survey and we should associate email with assessment id
          if (state.userSelections.length > 0) {
            setIsSubmittingForm(true);
            await associateEmail(email);
            setIsSubmittingForm(false);
            track(MIXPANEL_EVENTS.ASSOCIATE_EMAIL);
          }
          track(MIXPANEL_EVENTS.BUTTON_CLICK, { button: 'check-email' });
          navigate({ to: PAGE_URLS.SIGNUP });
        } catch (error) {
          logger(error);
        }
        break;
      case USER_EMAIL_STATUS_RES.EXISTS:
        navigate({ to: PAGE_URLS.UNDER_CONSTRUCTION });
        break;
      default:
        const error =
          emailCheckRes === USER_EMAIL_STATUS_RES.NOT_INITIALIZED
            ? STRINGS.ERRORS.USER_NOT_INITIALISED_ERROR
            : emailCheckRes;
        dispatch({
          type: FlexAssessmentContextAction.showNotification,
          payload: {
            type: NotificationType.Error,
            isShowing: true,
            message: `${STRINGS.ERRORS.SOMETHING_WRONG_RETRY}. Error: ${error}`,
          },
        });
        break;
    }
  };

  return (
    <form className={styles.modalContent}>
      <h3 className={styles.modalTitle}>Let's get started!</h3>
      <fieldset>
        <label
          className={clsx('form-input', 'email-input', {
            'is-valid': isValid === true,
            'has-error': isValid === false,
            [isPristine ? 'is-pristine' : 'is-touched']: true,
          })}
          htmlFor={EMAIL_FIELD_NAME}
        >
          <LabelAndError
            required={true}
            isValid={isValid}
            placeholder="Your email address"
            errorString={message}
          />
          <input
            id={EMAIL_FIELD_NAME}
            type="email"
            inputMode="email"
            name={EMAIL_FIELD_NAME}
            value={email}
            onChange={onUpdateHandler}
            onBlur={checkForTypos}
            autoComplete="email"
            required
            data-testid={EMAIL_FIELD_NAME}
          />
          {isEmailVerificationInProgress && <Spinner />}
        </label>
        <div className="email-typo-suggestion">
          {emailSuggestion && (
            <>
              Did you mean{' '}
              <button onClick={replaceEmailWithSuggestion}>
                {emailSuggestion.full}
              </button>
              ?
            </>
          )}
        </div>
      </fieldset>
      <EmailPrivacyNotification />
      <button
        onClick={submit}
        className={buttons.submitBtn}
        disabled={!isValid || isSubmittingForm || isEmailVerificationInProgress}
        data-testid="emailCheckSubmit"
      >
        {isSubmittingForm && <Spinner color={SPINNER_COLORS.LIGHT} />}
        Continue
      </button>
    </form>
  );
});
